// @ts-strict-ignore
import cloneDeep from 'lodash/cloneDeep';
import {AttrsSchema, Component} from 'panel';
import debounce from 'lodash/debounce';
import pick from 'lodash/pick';
import capitalize from 'lodash/capitalize';

import {AUTOMATED_CUSTOM_PROPERTY_TRACKING} from 'common/constants';
import {propertiesUtils} from 'common/report/util/properties';
import {CustomProperty} from 'common/types/reports/query';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {customPropertyClientLegacy} from 'common/report/queries/app-api/custom-properties-client';
import {emitTrackEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {getOwnAttr} from 'common/util';
import arbselectorDefs from 'common/util/tern/definitions/arb-selector.json';
import {
  buildCreateEntityModalAttrs,
  isSharingEnabledForProject,
  normalizeCustomPropertyEntity,
} from 'common/report/util/saved-entities';
import {EntityType, SharableEntityType, SubEntityType, UserRole} from 'common/report/util/saved-entities/types';
import {FeatureGate, ResourceType, RestrictEntitySaving} from 'common/report/constants';
import {PRIMITIVE_MATH_TYPES} from 'common/types/reports/bookmark';
import {promptSharing} from 'common/widgets/sharing';
import {ApiResponseError} from 'common/util/shared-client';
import {EntrySectionUpdates, QueryEventEntry, RowTypeOption} from 'common/query-builder/query-entry-section/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {Property} from 'common/query-builder/query-block/properties-menu/types';
import {CurrentProject} from 'common/report/model-resources/projects';
import customPropertiesShareConfig from 'shared_configs/custom_properties.json';
import {
  eventPropertyDefinitionsStore,
  fetchGlobalEventPropertyDefinitions,
  fetchPropertyDefinitionsForUserProfiles,
  peoplePropertyDefinitionsStore,
} from 'common/report/stores/property-definitions';
import {useStore} from 'common/util/component-helpers';
import {userDetailsStore} from 'common/report/stores/user-details';
import {sharedEntitiesClient} from 'common/report/queries/app-api/shared-entities-client';
import {getProjectRoleName} from 'common/widgets/sharing/utils';
import {currentProjectStore} from 'common/report/stores/project-details';

import {CustomPropertyBuilderErrors, CustomPropertyBuilderState} from './types';
import {
  canUseBorrowedProperties,
  getBorrowedProperty,
  getInlineCustomPropertyId,
  isInlineCustomProperty,
  isPersistedCustomProperty,
  trackingDataForBorrowedProperty,
} from './util';
import css from './index.inline.styl';
import {TRACKING_EVENTS} from './constants';
import {template} from './template';
import {ChangeEventDetail, CUSTOM_PROPERTY_RESOURCE_TYPES, Editor, PERSISTENT_EMPTY_PROPERTY_TOKEN} from './editor';

export const DISPATCHED_EVENTS = {
  CANCEL: `cancel`,
  APPLY: `apply`,
  DELETE: `delete`,
};

enum ExampleProperty {
  ChannelClassifier = `ChannelClassifier`,
  UrlDomain = `UrlDomain`,
  ResizeRange = `ResizeRange`,
  MergeValues = `MergeValues`,
  ExtractRegex = `ExtractRegex`,
  ReplaceRegex = `ReplaceRegex`,
  DaysSinceSignup = `DaysSinceSignup`,
  EmailDomain = `EmailDomain`,
}

const ExamplePropertyNames = {
  [ExampleProperty.ChannelClassifier]: ({t}) => t(`custom-property.example.channel-classifier`),
  [ExampleProperty.UrlDomain]: ({t}) => t(`custom-property.example.url-domain`),
  [ExampleProperty.ResizeRange]: ({t}) => t(`custom-property.example.resize-range`),
  [ExampleProperty.MergeValues]: ({t}) => t(`custom-property.example.merge-values`),
  [ExampleProperty.ExtractRegex]: ({t}) => t(`custom-property.example.extract-regex`),
  [ExampleProperty.ReplaceRegex]: ({t}) => t(`custom-property.example.replace-regex`),
  [ExampleProperty.DaysSinceSignup]: ({t}) => t(`custom-property.example.days-since-signup`),
  [ExampleProperty.EmailDomain]: ({t}) => t(`custom-property.example.email-domain`),
};

const EXAMPLES_INLINE_PROPERTIES = {
  [ExampleProperty.ChannelClassifier]: `// Below is the default definition for marketing channels pre-created for you. You can use this as-is or modify it to suit your business needs.

// NOTE: This formula is made up of if-elses, which means definitions above take priority over definitions below. So please note the order if you are modifying the definition

// Define variables for types of domains.
LET(
  search, ["google", "bing", "yahoo"],
LET(
  social, ["linkedin", "facebook", "twitter", "instagram"],
LET(
  shopping, ["amazon", "alibaba", "stripe", "ebay", "etsy", "shopify", "walmart"],

IFS(
  // Defining "Paid Search"
  (@"""{"label":"UTM Source","propertyDefaultType":"string","resourceType":"event","type":"string","value":"utm_source"}"""@IN search OR @"""{"label":"Referring Domain","propertyDefaultType":"string","resourceType":"event","type":"string","value":"$referring_domain"}"""@IN search OR PARSE_URL(@"""{"label":"Referring Domain","propertyDefaultType":"string","resourceType":"event","type":"string","value":"$referring_domain"}"""@, "domain") IN search)
  AND @"""{"label":"UTM Medium","propertyDefaultType":"string","resourceType":"event","type":"string","value":"utm_medium"}"""@IN ["cpc","ppc","paidsearch"],
  "Paid search",

  // Defining "Organic Search"
  (@"""{"label":"UTM Source","propertyDefaultType":"string","resourceType":"event","type":"string","value":"utm_source"}"""@IN search OR @"""{"label":"Referring Domain","propertyDefaultType":"string","resourceType":"event","type":"string","value":"$referring_domain"}"""@IN search OR PARSE_URL(@"""{"label":"Referring Domain","propertyDefaultType":"string","resourceType":"event","type":"string","value":"$referring_domain"}"""@, "domain") IN search),
  "Organic search",

  // Defining "Social"
  (@"""{"label":"UTM Source","propertyDefaultType":"string","resourceType":"event","type":"string","value":"utm_source"}"""@IN social  OR @"""{"label":"Referring Domain","propertyDefaultType":"string","resourceType":"event","type":"string","value":"$referring_domain"}"""@IN social OR PARSE_URL(@"""{"label":"Referring Domain","propertyDefaultType":"string","resourceType":"event","type":"string","value":"$referring_domain"}"""@, "domain") IN social),
  "Social",

  // Defining "Shopping"
  (@"""{"label":"UTM Source","propertyDefaultType":"string","resourceType":"event","type":"string","value":"utm_source"}"""@IN shopping  OR @"""{"label":"Referring Domain","propertyDefaultType":"string","resourceType":"event","type":"string","value":"$referring_domain"}"""@IN shopping OR PARSE_URL(@"""{"label":"Referring Domain","propertyDefaultType":"string","resourceType":"event","type":"string","value":"$referring_domain"}"""@, "domain") IN shopping),
  "Shopping",

  // Defining "Email"
  @"""{"label":"UTM Medium","propertyDefaultType":"string","resourceType":"event","type":"string","value":"utm_medium"}"""@= "email", "Email",

  // Defining "Display"
  @"""{"label":"UTM Medium","propertyDefaultType":"string","resourceType":"event","type":"string","value":"utm_medium"}"""@IN ["display","cpm","banner"], "Display",

  // Defining "Other Advertising"
  DEFINED(@"""{"label":"UTM Source","propertyDefaultType":"string","resourceType":"event","type":"string","value":"utm_source"}"""@) OR DEFINED(@"""{"label":"UTM Medium","propertyDefaultType":"string","resourceType":"event","type":"string","value":"utm_medium"}"""@),"Other Advertising",

  // Excluding your own website's domain so it doesn't get counted as "Referral".
  @"""{"label":"Referring Domain","propertyDefaultType":"string","resourceType":"event","type":"string","value":"$referring_domain"}"""@ in @"""{"label":"Current URL","propertyDefaultType":"string","resourceType":"event","type":"string","value":"$current_url"}"""@, UNDEFINED,

  // Defining "Referral".
  NOT @"""{"label":"Referring Domain","propertyDefaultType":"string","resourceType":"event","type":"string","value":"$referring_domain"}"""@="" AND DEFINED(@"""{"label":"Referring Domain","propertyDefaultType":"string","resourceType":"event","type":"string","value":"$referring_domain"}"""@),"Referral",

  // Defining "Direct". If there is no tracked UTM Source, UTM Medium or Referring Domain, channel is deemed "Direct"
  TRUE, "Direct"
))))`,
  [ExampleProperty.UrlDomain]: `// You can extract different parts of a URL from a URL string using the PARSE_URL function.
    // Choose a string property (e.g. http://www.google.co.uk/search/abc), and then the part of the URL you're interested in.
    PARSE_URL(${PERSISTENT_EMPTY_PROPERTY_TOKEN}, "domain")`,
  [ExampleProperty.ResizeRange]: `// Select a numeric property. In this example the property is assumed to
// be the time a user took to do an action, in minutes. This formula
// groups durations based on specified ranges.
IFS(
  ${PERSISTENT_EMPTY_PROPERTY_TOKEN} < 60, "Less than 1 hour",
  ${PERSISTENT_EMPTY_PROPERTY_TOKEN} < 120, "More than 1 hour, less than 2 hours",
  ${PERSISTENT_EMPTY_PROPERTY_TOKEN} < 240, "More than 2 hours, less than 4 hours",
  ${PERSISTENT_EMPTY_PROPERTY_TOKEN} < 360, "More than 4 hours, less than 6 hours",
  ${PERSISTENT_EMPTY_PROPERTY_TOKEN} >= 360, "More than 6 hours"
)`,
  [ExampleProperty.MergeValues]: `// Select a string property. In this example the property is assumed to
// be a string with values like "iOS 9", "iOS 10", "Mozilla", "Firefox",
// that can be grouped into larger categories.
IFS(
  "iOS" in ${PERSISTENT_EMPTY_PROPERTY_TOKEN}, "Apple",
  "Mozilla" in ${PERSISTENT_EMPTY_PROPERTY_TOKEN} or "Firefox" in ${PERSISTENT_EMPTY_PROPERTY_TOKEN}, "Firefox",

  // Since this last case is always TRUE, if none of the above
  // conditions are met, this formula will evaluate to the property
  // selected here
  TRUE, ${PERSISTENT_EMPTY_PROPERTY_TOKEN}
)`,
  [ExampleProperty.ExtractRegex]: `// Select a string property. The property is assumed to be a
// URL (e.g. "https://mixpanel.com/report/123"). This formula extracts
// "mixpanel.com" from the URL using a regular expression. There is
// also a REGEX_MATCH() function.
REGEX_EXTRACT(${PERSISTENT_EMPTY_PROPERTY_TOKEN}, "https?:\\/\\/([^\\/]+)", 1)`,
  [ExampleProperty.ReplaceRegex]: `// Select a string property. The property is assumed to be a dollar
// amount with commas in it (e.g. "$4,567"). This formula replaces the
// dollar sign and commas with the empty string, allowing the values to be
// cast to numbers and compared.
LET(amount, NUMBER(REGEX_REPLACE(${PERSISTENT_EMPTY_PROPERTY_TOKEN}, "[$,]", "")),
  IFS(
    amount < 200, "Low",
    amount < 1000, "Medium",
    TRUE, "High",
  )
)`,
  [ExampleProperty.DaysSinceSignup]: `// Select two different datetime properties.
// A good selection for the first property would be a user property that
// records the user's sign up date, and a good selection for the second
// property would be the "Date" event property. With these selections
// this custom property will evaluate to the number of days after sign
// up that a user performed each event.
DATEDIF(${PERSISTENT_EMPTY_PROPERTY_TOKEN}, ${PERSISTENT_EMPTY_PROPERTY_TOKEN}, "D")`,
  [ExampleProperty.EmailDomain]: `// You can split a string into different parts based on a user-specified delimiter using the SPLIT function.
// Choose a string property (e.g. dwightschrute@dundermifflin.com), and then the delimiter (in the case of an email domain, it would be @) and then which delimited part you'd like (2, because we want the part after @)
SPLIT(${PERSISTENT_EMPTY_PROPERTY_TOKEN}, "@", 2)`,
};
const HELP_DOC = `https://help.mixpanel.com/hc/en-us/articles/360030848432`;

const COMPONENT_NAME = `mp-custom-property-modal`;

const BEHAVIOR_QUERY_ENTRY_FIELDS = [
  `event`,
  `dateRange`,
  `dataGroupId`,
  `filters`,
  `filtersOperator`,
  `property`,
  `aggregationOperator`,
];

export type CustomPropertyBuilderAttrs = {
  'all-data-groups'?: boolean;
  'behavioral-properties-mode'?: boolean;
  'data-group-id'?: string;
  'default-name'?: string;
  'enable-workspace-filter-restrictions'?: boolean;
  'show-borrowed-properties'?: boolean;
  'event-names'?: string[];
  property?: Partial<Property>;
  'project-id'?: number;
  'user-id'?: number;
  open?: boolean;
  'track-name'?: string;
  'workspace-id'?: number;
};

type CustomPropertyBuilderProps = {
  attrs?: CustomPropertyBuilderAttrs;
};

export class CustomPropertyBuilder extends Component<
  CustomPropertyBuilderState,
  CustomPropertyBuilderAttrs,
  EmptyObj,
  EmptyObj,
  IronContextRegistry
> {
  declare editor: Editor;

  static get attrsSchema(): AttrsSchema<CustomPropertyBuilderAttrs> {
    return {
      ...super.attrsSchema,
      'all-data-groups': `boolean`,
      'behavioral-properties-mode': `boolean`,
      'data-group-id': `string`,
      'default-name': `string`,
      'enable-workspace-filter-restrictions': `boolean`,
      'show-borrowed-properties': `boolean`,
      'event-names': {
        type: `json`,
        default: [],
        description: `event names in the funnel steps`,
      },
      property: {
        type: `json`,
        default: null,
        description: `the inline or persisted custom property to render.`,
      },
      'project-id': `number`,
      'user-id': `number`,
      open: `boolean`,
      'track-name': {
        type: `string`,
        description: `If specified, dispatches '[mp-custom-property-modal] <event>' bubbled events that can be listened for tracking with "Report name" as a property`,
      },
      'workspace-id': `number`,
    };
  }

  constructor() {
    super();
  }

  static get dispatchedEvents() {
    return Object.values(DISPATCHED_EVENTS);
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `property`) {
      const property = this.attr(attr) || {};
      const {customPropertyId} = property;
      let {customProperty, resourceType} = property;
      if (!resourceType) {
        if (isPersistedCustomProperty(customPropertyId) && !customProperty) {
          customProperty = customPropertiesStore.value[customPropertyId];
          // make a copy to not modify the store directly
          customProperty = cloneDeep(customProperty);
        }
        if (customProperty) {
          resourceType = customProperty.resourceType;
        }
      }
      if (!customProperty) {
        customProperty = this.newCustomProperty(this.helpers.isBehavioralProperty());
      }
      // custom properties use 'events' and 'people' instead of 'event' and 'user'
      // TODO (safdar): change this to DTRT when/if custom properties are migrated to the QueryEntry format
      if (Object.keys(CUSTOM_PROPERTY_RESOURCE_TYPES).includes(resourceType)) {
        resourceType = CUSTOM_PROPERTY_RESOURCE_TYPES[resourceType];
      }
      this.update({
        customPropertyId,
        customProperty,
        resourceType,
        toCreate: null,
      });
      this.updateQueryEntriesFromBehavior();
    }
  }

  get config() {
    return {
      ...super.config,
      css,
      useShadowDom: true,
      template,
      contexts: [`currentProject` as const, `i18n` as const],
      defaultState: {
        activePropertyIndex: null,
        autocompleteOpen: false,
        autocompletePopup: null,
        currentAutocompleteLine: null,
        customProperty: this.newCustomProperty(),
        customPropertyExampleName: null,
        customPropertyId: null,
        errors: {},
        extractionError: null,
        queryEntries: [] as QueryEventEntry[],
        toDelete: false,
        toCreate: null,
      },
      helpers: {
        getBehavioralAggregationMethods: () => [
          PRIMITIVE_MATH_TYPES.TOTAL,
          PRIMITIVE_MATH_TYPES.AVERAGE,
          PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
          PRIMITIVE_MATH_TYPES.MOST_FREQUENT,
          PRIMITIVE_MATH_TYPES.FIRST_VALUE,
        ],
        isSavingDisabled: () => {
          if (this.usesBorrowedProperty()) {
            if (this.validateOnDisplayFormulaChange()) {
              // formula contains other content besides the borrowed property
              return true;
            }
            if (!this.currentProject?.isDemo) {
              if (!this.canWriteBorrowedProperty()) {
                // doesn't have needed role to create/update borrowed prop
                return true;
              }
              if (this.getNumRemainingBorrowedProperties() <= 0 && !this.helpers.isEdit()) {
                // reached project limit for borrowed properties
                return true;
              }
            }
            return false;
          } else if (this.helpers.isBehavioralProperty()) {
            return this.getQueryEntries().length === 0;
          }
          return false;
        },
        isSavingRestricted: () => !this.currentProject?.canSaveEntity(RestrictEntitySaving.CustomProperties),
        openPricingUpsell: () => {
          this.track(TRACKING_EVENTS.CLICK_UPSELL, {
            'Feature Limit': FeatureGate.RestrictEntitySaving,
            'Entity Type': `custom_properties`,
          });
          window.open(`https://mixpanel.com/pricing/`, `_blank`).focus();
        },
        getBehavioralSelectorTypes: (): RowTypeOption[] => {
          return [
            {
              value: ResourceType.Event,
              includeDateRange: true,
            },
          ];
        },
        buildCreateEntityModalAttrs: () => {
          const i18n = this.getContext(`i18n`);
          const attrs = buildCreateEntityModalAttrs({
            projectId: this.attr(`project-id`),
            entityType: EntityType.CustomProperty,
            ...(this.usesBorrowedProperty() && {subEntityType: SubEntityType.BorrowedProperty}),
            entity: normalizeCustomPropertyEntity(this.state.customProperty),
            modalOpen: this.attr(`open`),
            isEditing: false,
            isDuplicating: false,
            showShareManagement: this.helpers.hasSharingFeatureFlag(),
            i18n,
          });

          const eventPropertyDisplayNames = Object.values(eventPropertyDefinitionsStore.value)
            .filter((prop) => !prop.customPropertyId)
            .map((property) => propertiesUtils.renameProperty(property.name, ResourceType.Event, i18n));
          const userPropertyDisplayNames = Object.values(peoplePropertyDefinitionsStore.value)
            .filter((prop) => !prop.customPropertyId)
            .map((property) => propertiesUtils.renameProperty(property.name, ResourceType.User, i18n));

          const propertyDisplayNames = [...eventPropertyDisplayNames, ...userPropertyDisplayNames];
          const matchesTrackedPropertyName = propertyDisplayNames.some((propertyDisplayName) => {
            return this.state.customProperty.name === propertyDisplayName;
          });

          if (!attrs.warning && matchesTrackedPropertyName) {
            attrs.warning = this.t(`custom-property.error.name-exists`);
          }
          return attrs;
        },

        isBehavioralProperty: (customProperty = this.state.customProperty) => {
          return !!getOwnAttr(customProperty, `behavior`) || this.attr(`behavioral-properties-mode`);
        },

        tooltipClicked: () => window.open(HELP_DOC, ``).focus(),
        updateName: debounce((name: string) => {
          this.updateField(`name`, name, Boolean);
        }, 250),
        updateDescription: (ev) => {
          const customProperty = {...this.state.customProperty, description: ev.target.value};
          this.update({customProperty});
        },
        getName: () => this.state.customProperty.name,
        getDescription: () => this.state.customProperty.description,
        isEdit: () => {
          const property = this.attr(`property`);
          return property && (property.customPropertyId || property.customProperty);
        },
        isPersisted: () => isPersistedCustomProperty(this.state.customPropertyId),
        hasSharingFeatureFlag: () => isSharingEnabledForProject(this.currentProject),
        getHeaderText: () => {
          const contextName = this.helpers.isBehavioralProperty() ? `behavior` : `property`;

          if (this.helpers.isPersisted()) {
            return this.t(`custom-property.header.update`, {context: contextName});
          } else if (isInlineCustomProperty(this.state.customPropertyId)) {
            return this.t(`custom-property.header.edit`, {context: contextName});
          }
          return this.t(`custom-property.header.create`, {context: contextName});
        },
        getResourceType: () => this.state.resourceType,
        getResourceTypeText: () => {
          return {
            [CUSTOM_PROPERTY_RESOURCE_TYPES[ResourceType.User]]: `Profile`,
            [CUSTOM_PROPERTY_RESOURCE_TYPES[ResourceType.Event]]: `Event`,
          }[this.helpers.getResourceType()];
        },
        modalStateChange: (ev) => {
          if (ev.detail && ev.detail.state === `closed`) {
            this.close();
          }
        },
        getPersistedCustomPropertyNames: () => {
          return Object.entries(customPropertiesStore.value)
            .filter((prop) => isPersistedCustomProperty(prop[0]))
            .map((prop) => prop[1].name);
        },
        getComposedProperties: () => this.getComposedProperties(),
        getDisplayFormula: () => this.state.customProperty.displayFormula,
        closeModal: () => this.close(),
        setToDelete: () => this.update({toDelete: true}),
        deleteProperty: () => {
          const customProperty = customPropertiesStore.value[this.state.customPropertyId];
          // TODO: handle network blip, etc, when deleting a property
          this.deleteProperty(this.state.customPropertyId);
          this.trackWithState(TRACKING_EVENTS.DELETE, this.state.customPropertyId, customProperty, false, {});
          this.dispatchEvent(
            new CustomEvent(DISPATCHED_EVENTS.DELETE, {
              detail: {
                customPropertyId: this.state.customPropertyId,
              },
            }),
          );
        },
        handleDeleteConfirmChange: (visibility) => {
          if (visibility !== `open`) {
            this.update({toDelete: false});
          }
        },
        getExampleSections: () => {
          return [
            {
              items: Object.keys(ExampleProperty).map((example) => ({
                value: example,
                label: this.getContext(`i18n`).print(ExamplePropertyNames[example]),
              })),
            },
          ];
        },
        handleInsertExample: (ev) => {
          const {print} = this.getContext(`i18n`);
          const exampleName = ev.detail.item.value;
          this.update({
            customPropertyExampleName: exampleName,
          });
          this.trackWithState(
            TRACKING_EVENTS.INSERT_EXAMPLE,
            this.state.customPropertyId,
            this.state.customProperty,
            false,
            {},
            {
              Example: print(ExamplePropertyNames[ev.detail.item.value], `en`),
            },
          );
          this.editor.setDisplayFormula(EXAMPLES_INLINE_PROPERTIES[ev.detail.item.value], {});
          this.editor.focus();
        },
        // TODO opslead
        applyProperty: async (toCreate) => {
          let errors: CustomPropertyBuilderErrors = {};
          if (!this.helpers.getName()) {
            errors.name = this.t(`custom-property.error.required`);
          }
          if (!this.helpers.isBehavioralProperty()) {
            if (!this.helpers.getDisplayFormula()) {
              errors.displayFormula = this.t(`custom-property.error.required`);
            }
            const composedPropertiesError = this.validateComposedProperties();
            if (composedPropertiesError !== null) {
              errors.composedProperties = composedPropertiesError;
            }
          }
          if (Object.entries(errors).length > 0) {
            this.trackWithState(
              TRACKING_EVENTS.SAVE,
              this.state.customPropertyId,
              this.state.customProperty,
              toCreate,
              errors,
            );
            this.update({errors});
            return;
          }

          let customProperty = {
            ...this.state.customProperty,
            resourceType: this.state.resourceType,
          };

          let result;
          let storeUpdate = {};
          const projectId = this.attr(`project-id`);
          let {customPropertyId} = this.state;
          try {
            if (this.state.extractionError) {
              throw new Error(`${this.t(`custom-property.error.description`)}: ${this.state.extractionError}`);
            }
            if (isPersistedCustomProperty(customPropertyId)) {
              // updating saved custom property
              result = await customPropertiesStore.put(customPropertyId, {
                projectId,
                customProperty,
                customPropertyId,
              });
            } else {
              // adding/updating inline or saving new custom property
              const response = await customPropertyClientLegacy.query({
                projectId,
                customProperty,
                validate: true,
                workspaceId: this.attr(`workspace-id`),
              });
              if (response.status === `error`) {
                throw new ApiResponseError(response, response.error);
              } else {
                result = response.results;
              }
            }
          } catch (err) {
            // Handle regular Error
            let field = `displayFormula`;
            let error = err.message;
            // Handle ApiResponseError
            if (err.response) {
              field = err.response.field;
              error = err.response.error;
            }
            errors = {[field]: error};
            this.trackWithState(TRACKING_EVENTS.SAVE, customPropertyId, customProperty, toCreate, errors);
            this.update({errors});
            return;
          }

          const propertyType = result && result.propertyType;
          customProperty = {...customProperty, propertyType};

          if (!result || !result.customPropertyId) {
            // inline, we give it a session only UID if none has been given before
            customPropertyId = getInlineCustomPropertyId(customPropertyId);
            // and manually set it to the cache
            storeUpdate = {[customPropertyId]: customProperty as CustomProperty};
          }

          if (toCreate) {
            this.update({
              toCreate: customProperty,
            });
            this.trackWithState(TRACKING_EVENTS.SAVE_NEW, customPropertyId, customProperty, toCreate, {});
          } else {
            customPropertiesStore.update(storeUpdate);
            this.trackWithState(TRACKING_EVENTS.SAVE, customPropertyId, customProperty, toCreate, {});
            this.dispatchEvent(
              new CustomEvent(DISPATCHED_EVENTS.APPLY, {
                detail: {
                  property: {
                    customProperty,
                    customPropertyId,
                    resourceType: this.state.resourceType,
                    propertyType,
                    type: propertyType,
                    value: customProperty.name,
                  },
                },
              }),
            );
          }
        },
        handleCreate: async (ev) => {
          const {name, description} = ev.detail.value;
          const customProperty: CustomProperty = {
            ...this.state.toCreate,
            exampleValue: ``,
            name,
            description,
          };
          const projectId = this.attr(`project-id`);
          const result = await customPropertiesStore.post({
            projectId,
            customProperty,
            workspaceId: this.attr(`workspace-id`),
          });
          if (result && result.customPropertyId) {
            const customPropertyId = result.customPropertyId;
            if (this.state.customPropertyId && !isPersistedCustomProperty(this.state.customPropertyId)) {
              // we just saved an inline customProperty to a persisted one
              customPropertiesStore.update({
                [this.state.customPropertyId]: {
                  ...customPropertiesStore.value[this.state.customPropertyId],
                  newCustomPropertyId: customPropertyId,
                },
              });
            }
            this.trackWithState(
              TRACKING_EVENTS.SAVE,
              customPropertyId,
              {...customProperty, customPropertyId},
              true,
              {},
              {
                'is saving new': true,
              },
            );
            this.dispatchEvent(
              new CustomEvent(DISPATCHED_EVENTS.APPLY, {
                detail: {
                  property: {
                    customProperty,
                    customPropertyId,
                    resourceType: this.state.resourceType,
                    propertyType: customProperty.propertyType,
                    propertyDefaultType: customProperty.propertyType,
                    type: customProperty.propertyType,
                    value: customProperty.name,
                  },
                },
              }),
            );

            if (this.usesBorrowedProperty(customProperty)) {
              // borrowed properties are public/editable by default
              await sharedEntitiesClient.updateGrants(
                {projectId, entityId: result.customPropertyId, entityType: SharableEntityType.CustomProperty},
                {projectShares: [{id: projectId, canEdit: true}]},
              );
            }

            // When Save and Share button is clicked, open a sharing modal
            if (ev.type === `saveAndShare`) {
              await promptSharing({
                type: SharableEntityType.CustomProperty,
                id: customPropertyId,
                'project-id': projectId,
              });
            }
          }
        },
        handleCancelCreate: () => {
          this.update({toCreate: null});
        },
        handleEditorConnect: (ev) => {
          this.update({
            autocompletePopup: ev.detail.autocompletePopup,
          });
        },
        handleEditorAutocompleteChange: (ev) => {
          const updates = {tooltipData: null};
          if (ev.detail.autocompleteOpen !== undefined) {
            updates[`autocompleteOpen`] = ev.detail.autocompleteOpen;
          }
          if (ev.detail.currentAutocompleteLine !== undefined) {
            updates[`currentAutocompleteLine`] = ev.detail.currentAutocompleteLine;
          }
          if (ev.detail.autocompleteOpen || this.state.autocompleteOpen) {
            if (ev.detail.menuTooltipData !== undefined) {
              updates[`tooltipData`] = ev.detail.menuTooltipData;
            }
          } else {
            if (ev.detail.cursorTooltipData !== undefined) {
              updates[`tooltipData`] = ev.detail.cursorTooltipData;
            }
          }
          this.update(updates);
        },
        handleValueChange: (ev: CustomEvent<ChangeEventDetail>) => {
          ev.stopPropagation();
          this.updateField(`displayFormula`, ev.detail.displayFormula, Boolean);
          this.update({extractionError: ev.detail.extractionError});
          this.updateComposedProperties(ev.detail.composedProperties);
          const displayFormulaError = this.validateOnDisplayFormulaChange();
          if (displayFormulaError) {
            this.update({errors: {...this.state.errors, displayFormula: displayFormulaError}});
          }
        },
        handleEditorElementInsert: (vnode) => {
          this.editor = vnode.elm;
        },
        getQueryEntries: () => this.getQueryEntries(),
        handleQueryEventsSectionUpdate: (updates: EntrySectionUpdates<QueryEventEntry>) => {
          this.updateQueryEntries(updates.entries);
          this.updateBehaviorFromQueryEntries();
        },
      },
    };
  }

  propertyDefinitionsStoreCallback = () => {
    this.update();
  };

  connectedCallback() {
    super.connectedCallback();
    useStore(this, userDetailsStore).fetch();
    fetchGlobalEventPropertyDefinitions({
      callback: this.propertyDefinitionsStoreCallback,
    });
    fetchPropertyDefinitionsForUserProfiles({
      callback: this.propertyDefinitionsStoreCallback,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    eventPropertyDefinitionsStore.unsubscribe(this.propertyDefinitionsStoreCallback);
    peoplePropertyDefinitionsStore.unsubscribe(this.propertyDefinitionsStoreCallback);
  }

  get currentProject(): CurrentProject | null {
    return this.getContext(`currentProject`).get().value;
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  isEditorReady(): boolean {
    // wait until borrowed props featureflag is ready before rendering the editor. Otherwise,
    // it causes issues with editor if borrowed props is initially disabled and then enabled.
    return typeof this.canUseBorrowedProperties() === `boolean`;
  }

  canUseBorrowedProperties(): boolean | undefined {
    return canUseBorrowedProperties(this.currentProject);
  }

  usesBorrowedProperty(customProperty = this.state.customProperty): boolean {
    return Boolean(getBorrowedProperty(customProperty));
  }

  getBorrowedPropertiesLimit(): number {
    const limitOverrides = customPropertiesShareConfig.BORROWED_PROPERTY_PROJECT_LIMIT_OVERRIDES;
    if (Object.prototype.hasOwnProperty.call(limitOverrides, this.currentProject.id)) {
      return limitOverrides[this.currentProject.id];
    }
    return customPropertiesShareConfig.BORROWED_PROPERTY_PROJECT_LIMIT;
  }

  getNumBorrowedProperties(): number {
    return Object.values(customPropertiesStore.value).filter((prop) => getBorrowedProperty(prop)).length;
  }

  getNumRemainingBorrowedProperties(): number {
    return this.getBorrowedPropertiesLimit() - this.getNumBorrowedProperties();
  }

  canWriteBorrowedProperty(): boolean {
    return (
      [UserRole.Owner, UserRole.Admin].includes(this.currentProject?.role?.name as UserRole) ||
      userDetailsStore.value.isStaff
    );
  }

  getBannerText(): string | null {
    if (!this.usesBorrowedProperty() || this.currentProject?.isDemo) {
      return null;
    }

    if (!this.canWriteBorrowedProperty()) {
      return this.t(
        `custom-property.error.borrowed-property-role-${this.helpers.isPersisted() ? `update` : `create`}`,
        {
          role: capitalize(getProjectRoleName(currentProjectStore.value)),
        },
      );
    }
    return this.t(`custom-property.error.borrowed-property-limit`, {
      remaining: this.getNumRemainingBorrowedProperties(),
      limit: this.getBorrowedPropertiesLimit(),
    });
  }

  close() {
    this.trackWithState(
      TRACKING_EVENTS.CANCEL,
      this.state.customPropertyId,
      this.state.customProperty,
      false,
      {},
      {had_interaction: this.editor?.hadInteraction},
    );
    this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.CANCEL));
  }

  getComposedProperties() {
    return this.state.customProperty.composedProperties;
  }

  updateComposedProperties(composedProperties) {
    this.updateField(`composedProperties`, composedProperties, (value) => Object.entries(value).length > 1);
  }

  validateOnDisplayFormulaChange(): string | null {
    const isSinglePropertyFormula = (this.helpers.getDisplayFormula() || ``).trim() === `_A`;
    if (this.usesBorrowedProperty() && !isSinglePropertyFormula) {
      return this.t(`custom-property.error.borrowed-property-use`);
    }
    return null;
  }

  validateComposedProperties() {
    const composedProperties = this.getComposedProperties();
    for (const letter of Object.keys(composedProperties)) {
      const property = composedProperties[letter];
      // TODO: should check for recursive references within behaviors as well
      if (
        property.customPropertyId &&
        this.state.customPropertyId &&
        property.customPropertyId === this.state.customPropertyId
      ) {
        return this.t(`custom-property.error.recursive-variable`, {name: letter});
      }
      if (
        property.customPropertyId &&
        this.state.customProperty.referencedBy &&
        this.state.customProperty.referencedBy.includes(property.customPropertyId)
      ) {
        return this.t(`custom-property.error.loop-variable`, {name: letter, other: property.label});
      }
    }
    return null;
  }

  getQueryEntries() {
    return this.state.queryEntries;
  }

  updateQueryEntries(queryEntries: QueryEventEntry[]) {
    this.update({queryEntries});
  }

  updateBehaviorFromQueryEntries() {
    this.updateField(
      `behavior`,
      {
        ...pick(this.getQueryEntries()[0], BEHAVIOR_QUERY_ENTRY_FIELDS),
      },
      Boolean,
    );
  }

  updateQueryEntriesFromBehavior() {
    const queryEntries: QueryEventEntry[] = [];
    if (this.state.customProperty.behavior) {
      queryEntries.push({
        ...(pick(this.state.customProperty.behavior, BEHAVIOR_QUERY_ENTRY_FIELDS) as QueryEventEntry),
        type: ResourceType.Event,
      });
    }
    this.updateQueryEntries(queryEntries);
  }

  updateField(field, value, toClearErrorFunc) {
    const customProperty = {...this.state.customProperty, [field]: value};
    const {errors} = this.state;
    if (toClearErrorFunc(value) && errors[field]) {
      errors[field] = null;
    }
    this.update({customProperty, errors});
  }

  deleteProperty(customPropertyId) {
    customPropertiesStore.delete(customPropertyId, {
      projectId: this.attr(`project-id`),
      customPropertyId,
    });
  }

  newCustomProperty(forBehavioralProperty = false) {
    if (forBehavioralProperty) {
      return {
        name: this.attr(`default-name`),
        description: ``,
        behavior: null,
      } as CustomProperty;
    }
    return {
      name: this.attr(`default-name`),
      description: ``,
      displayFormula: ``,
      composedProperties: {},
    } as CustomProperty;
  }

  track(event, props) {
    emitTrackEvent(this, {
      eventName: event,
      eventPrefix: COMPONENT_NAME,
      props: {
        ...props,
        project_id: this.attr(`project-id`),
        tab: this.attr(`track-name`),
      },
      cancelable: false,
    });
  }

  // TODO opslead
  trackWithState(
    eventType: string,
    customPropertyId,
    customProperty: CustomProperty,
    toCreate,
    errors,
    extraProps = {},
  ) {
    const {name, displayFormula, resourceType, propertyType} = customProperty;
    const keywords = Object.keys(arbselectorDefs)
      .filter((keyword) => !keyword.startsWith(`!`))
      .map((keyword) => (keyword.endsWith(`()`) ? keyword.slice(0, -2) : keyword));
    const functionsUsed = keywords.filter((keyword) => {
      const regex = new RegExp(`\\b${keyword}\\b\\s*\\(`, `gi`);
      // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
      return regex.test(displayFormula || ``);
    });
    const errorCount = Object.entries(errors).length;

    // populate behavioral property tracking data
    if (this.helpers.isBehavioralProperty() && this.getQueryEntries().length) {
      const entry = this.getQueryEntries()[0];
      extraProps = {
        ...extraProps,
        'is behavioral property': true,
        Aggregator: entry.aggregationOperator,
        'Per-user aggregator': entry.aggregationOperatorPerUser,
        Property: entry.property?.label,
        '# of filters': entry.filters.length,
        'filters operator': entry.filtersOperator,
        'date range from': entry.dateRange.from || null,
        'date range to': entry.dateRange.to || null,
        'date range type': entry.dateRange.type,
        'window unit': entry.dateRange.window ? entry.dateRange.window.unit : null,
        'window value': entry.dateRange.window ? entry.dateRange.window.value : null,
      };
    } else {
      extraProps = {
        ...extraProps,
        name: customProperty.name,
        'resource type': customProperty.resourceType,
        'is behavioral property': false,
        displayFormula,
        Example: this.state.customPropertyExampleName,
        'composed properties': Object.values(this.getComposedProperties()).map((p) => p.value),
        [`# of composedProperties`]: this.getComposedProperties()
          ? Object.entries(this.getComposedProperties()).length
          : 0,
        'borrowed property': trackingDataForBorrowedProperty(customProperty),
        '# of borrowed properties in project': Object.values(customPropertiesStore.value)
          .map(getBorrowedProperty)
          .filter(Boolean).length,
      };
    }

    if (customProperty.pre_populated_entity_id) {
      extraProps = {
        ...extraProps,
        [AUTOMATED_CUSTOM_PROPERTY_TRACKING]: customProperty.pre_populated_entity_id,
      };
    }

    const props = {
      customPropertyId,
      name,
      persisted: toCreate || customPropertyId || false,
      resourceType,
      propertyType,
      ...(errorCount > 0 ? {errors} : {}),
      'functions used': functionsUsed,
      ...extraProps,
    };

    this.track(eventType, props);
  }
}

// TODO config accesses attr, need to fix before moving to react
export const CustomPropertyBuilderEl = defineAndMakeJsxFactory<CustomPropertyBuilderProps>(
  COMPONENT_NAME,
  CustomPropertyBuilder,
);
